/**
 * Finding the rows a single metric confirmation can be applied to.
 *
 * Confirming a record's AI-suggested metric promotes THAT row and teaches a
 * per-client alias — but the alias is read at ingestion time, so identical rows
 * already extracted keep their original mapping until a human touches them. This
 * module finds those rows so the reviewer can be offered them in one action.
 *
 * WHICH rows qualify is the load-bearing decision here:
 * - included — the AI suggested the SAME metric (confirming them restates the
 *   identical judgement), or the row is UNMAPPED (applying the metric fills a blank);
 * - EXCLUDED — the AI suggested a DIFFERENT metric. The alias key is the label alone,
 *   so a future upload WOULD override those rows; doing it retroactively would
 *   overwrite the model's per-row context, and any prior human edit, unseen and in
 *   bulk. A row where the model disagreed with the new rule is a signal worth an
 *   individual look, not noise to flatten.
 */

import { fetchAllRecordIdsWithLabel } from '../../../services/syntiro_honey/data_ingestion.service';
import { EsgRecordUpdateResponse, RecordListQuery } from '../../../types/syntiro_honey';

/** The rows a confirmation may be applied to, with the metric that was confirmed. */
export interface SiblingConfirmTarget {
  /** The confirmed record's raw label, as shown to the reviewer. */
  rawLabel: string;
  /** The metric to apply to every id below. */
  metricId: string;
  /** The metric's display name, or null when the response carried none. */
  metricName: string | null;
  /** Ids of the qualifying rows, excluding the record that was just confirmed. */
  ids: Array<string>;
}

/**
 * Finds the still-pending rows in the SAME FILE that share a confirmed record's raw
 * label and may take its metric (see the module note for the inclusion rule).
 *
 * Scoped to the confirmed record's file so the count matches what the reviewer is
 * looking at and never shifts with unrelated triage filters. Approved records are
 * excluded because they are immutable (the PATCH would 409), and rejected ones
 * because a mapping edit must not overturn a review verdict.
 *
 * @param {EsgRecordUpdateResponse} confirmed The PATCH response for the confirmed record.
 * @returns {Promise<SiblingConfirmTarget | null>} The target, or null when nothing
 *   qualifies or the lookup failed — the confirmation itself has already succeeded,
 *   so a failed OFFER must never surface to the reviewer as a failed confirm.
 */
export async function findSiblingsToConfirm(
  confirmed: EsgRecordUpdateResponse
): Promise<SiblingConfirmTarget | null> {
  const { id, metricId, metricName, rawLabel, fileId } = confirmed;
  if (metricId === null || rawLabel.trim() === '') {
    return null;
  }
  const scope: RecordListQuery = { fileId, rawLabel, excludeApproved: true };
  try {
    // fetchAllRecordIdsWithLabel pages past the list limit, so siblings on later
    // pages count.
    const [sameSuggestion, unmapped] = await Promise.all([
      fetchAllRecordIdsWithLabel({ ...scope, mappingStatus: 'SUGGESTED', metricId }),
      fetchAllRecordIdsWithLabel({ ...scope, mappingStatus: 'UNMAPPED' })
    ]);
    // Defence-in-depth: re-check the label client-side rather than trusting the
    // server's own `rawLabel` query param unconditionally. honey's list schema is
    // a non-strict zod object, so if that param is ever dropped server-side (a
    // rollback, a refactor, an older deployed version), the response would
    // silently include every unmapped/same-metric row in the whole file instead
    // of just this label's — and "Apply to all" would then remap and mis-teach
    // an alias for every one of them. This filter is the only thing that would
    // still catch that.
    const targetLabel = rawLabel.toLocaleLowerCase();
    const matching = [...sameSuggestion, ...unmapped].filter(
      (record) => record.rawLabel.toLocaleLowerCase() === targetLabel
    );
    // The confirmed row is EXACT by now so it should not appear, but exclude it
    // explicitly rather than depend on that.
    const ids = Array.from(new Set(matching.map((record) => record.id))).filter(
      (siblingId) => siblingId !== id
    );
    return ids.length === 0 ? null : { rawLabel, metricId, metricName, ids };
  } catch {
    return null;
  }
}
